import { FC, useEffect } from "react";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { UseFormReturn, useWatch } from "react-hook-form";

import { EditRecurrencesFormProps } from "./types";

import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { isSweepstakesCategory } from "@/common/helpers/sweepstakes";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardDateTimePicker } from "@/dashboard-design-system/DateTimePicker";
import { DashboardDivider } from "@/dashboard-design-system/Divider";
import { DashboardIcon } from "@/dashboard-design-system/Icon/DashboardIcon";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { useLiveFormEditorContext } from "@/features/LiveFormEditor/common/LiveFormEditorContext";
import { Delete, Idea } from "@/icons/outlined";

interface OpenAndCloseDateBundleProps {
  form: UseFormReturn<EditRecurrencesFormProps>;
  recurrenceIndex: number;
  timezone: string;
  onRemove: () => void;
}

export const OpenAndCloseDateBundle: FC<OpenAndCloseDateBundleProps> = ({
  form,
  recurrenceIndex,
  timezone,
  onRemove,
}) => {
  const { t } = useTranslate();
  const { locale } = useLocaleContext();
  const { formCategory } = useLiveFormEditorContext();
  const openDateField = `recurrences.${recurrenceIndex}.openDate` as const;
  const closeDateField = `recurrences.${recurrenceIndex}.closeDate` as const;
  const recurrenceErrors = form.formState.errors?.recurrences?.[recurrenceIndex];
  const openDateError = recurrenceErrors?.openDate;
  const closeDateError = recurrenceErrors?.closeDate;

  const openDate = useWatch({ control: form.control, name: openDateField });
  const closeDate = useWatch({ control: form.control, name: closeDateField });

  const atLeastOneDate = () => {
    if (!openDate && !closeDate) {
      return t("dashboard", "ticketing.salesAtLeastOneDateRequired") as string;
    }
    return true;
  };

  useEffect(() => {
    if (openDate || closeDate) {
      form.trigger([openDateField, closeDateField]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDate, closeDate, form]);

  return (
    <Grid item xs={12}>
      <Grid container spacing={2} alignItems="flex-start" wrap="nowrap">
        <Grid item xs={6}>
          <DashboardDateTimePicker
            name={openDateField}
            label={t("dashboard", "ticketing.salesOpenDateLabel")}
            control={form.control}
            locale={locale}
            size="small"
            rules={[
              {
                validate: {
                  atLeastOne: atLeastOneDate,
                  isBeforeClose: (value?: Date | null) => {
                    if (!value || !closeDate) {
                      return true;
                    }
                    return new Date(value) < new Date(closeDate)
                      ? true
                      : (t("dashboard", "ticketing.salesOpenAfterCloseError") as string);
                  },
                },
              },
            ]}
            errorMessage={openDateError && openDateError.message}
            helperText={t("dashboard", "ticketing.salesOpenDateHelper")}
            data-test="ticketing-recurrence-open-date"
            timezone={timezone}
          />
        </Grid>
        <Grid item xs={6}>
          <DashboardDateTimePicker
            name={closeDateField}
            label={t("dashboard", "ticketing.salesCloseDateLabel")}
            control={form.control}
            locale={locale}
            size="small"
            rules={[{ validate: { atLeastOne: atLeastOneDate } }]}
            errorMessage={closeDateError && closeDateError.message}
            helperText={t("dashboard", "ticketing.salesCloseDateHelper")}
            data-test="ticketing-recurrence-close-date"
            timezone={timezone}
            endElement={
              <DashboardIconButton onClick={onRemove} size="small" vibe="neutral">
                <Delete />
              </DashboardIconButton>
            }
          />
        </Grid>
      </Grid>
      <DashboardDivider sx={{ marginBlock: 2 }} />
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Box sx={{ display: "flex", flexShrink: 0, paddingBlockStart: "2px" }}>
          <DashboardIcon size="small" vibe="text-neutral-quiet">
            <Idea />
          </DashboardIcon>
        </Box>
        <DashboardTypography variant="body2" vibe="text-neutral-quiet" sx={{ flex: 1, minWidth: 0 }}>
          {t(
            "dashboard",
            isSweepstakesCategory(formCategory) ? "sweepstakes.dates.scheduleTip" : "ticketing.scheduleTipInformation"
          )}
        </DashboardTypography>
      </Stack>
    </Grid>
  );
};
